/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  StoryCard,
  storyCardApi,
  StoryCardsOrganisationTagFeedArgs,
  StoryCardsOrganisationTagFeedFilters,
} from '../services';
import {
  addCardToBookmarks,
  addComment,
  deleteCard,
  deleteComment,
  editComment,
  eventDelete,
  eventUpdateChannels,
  hideComment,
  highlightComment,
  pollVote,
  reactOnCard,
  reactOnComment,
  removeCardFromBookmarks,
  repostCard,
  toggleCardSection,
  togglePublishOnCard,
  updateCardEvent,
} from './common';
import {
  addCommentReducer,
  deleteCommentReducer,
  editCommentReducer,
  hideCommentReducer,
  highlightCommentReducer,
  pollVoteReducer,
  reactOnCardReducer,
  reactOnCommentReducer,
} from './extraReducers';

const tagsFeedHandler = async (args: StoryCardsOrganisationTagFeedArgs, { dispatch }: any) => {
  return dispatch(
    storyCardApi.endpoints.storyCardsOrganisationTagFeed.initiate({ ...args })
  ).unwrap();
};

export const getTagsFeed = createAsyncThunk('tagsFeed', tagsFeedHandler);

export const loadMoreTagsFeed = createAsyncThunk('tagsFeed/loadMore', tagsFeedHandler);

interface TagsFeedState {
  isFetched: boolean;
  tagsFeed: StoryCard[];
  page: number;
  hasNextPage: boolean;
  filters?: StoryCardsOrganisationTagFeedFilters;
}

const initialState: TagsFeedState = {
  isFetched: false,
  tagsFeed: [],
  page: 0,
  hasNextPage: false,
  filters: undefined,
};

const tagsFeedSlice = createSlice({
  name: 'tagsFeed',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTagsFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getTagsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;
        state.tagsFeed = items;
        state.page = page;
        state.hasNextPage = hasNextPage;
        state.filters = action.meta.arg.filters;
      })
      .addCase(loadMoreTagsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.tagsFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { itemId, comment } = action.payload;

        const card = state.tagsFeed.find(({ id }) => id === itemId);

        if (!card) {
          return;
        }

        addCommentReducer({ card, comment });
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        editCommentReducer({ card, comment });
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        hideCommentReducer({ card, comment });
      })
      .addCase(highlightComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        highlightCommentReducer({ card, comment });
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { deleted, cardId, commentId, parentId, repliesCount } = action.payload;

        if (!deleted) {
          return;
        }

        const card = state.tagsFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        deleteCommentReducer({ card, commentId, parentId, repliesCount });
      })
      .addCase(reactOnComment.fulfilled, (state, action) => {
        const { reactions, cardId, commentId } = action.payload;

        const card = state.tagsFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        reactOnCommentReducer({ card, commentId, reactions });
      })
      .addCase(reactOnCard.fulfilled, (state, action) => {
        const { cardId, reaction, reactions } = action.payload;

        const card = state.tagsFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        reactOnCardReducer({ card, reaction, reactions });
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        const { deleted, storyCardId } = action.payload;

        if (!deleted) {
          return;
        }

        state.tagsFeed = state.tagsFeed.filter(({ id }) => id !== storyCardId);
      })
      .addCase(repostCard.fulfilled, (state, action) => {
        const storyCard = action.payload;

        if (!storyCard) {
          return;
        }

        const cardIndex = state.tagsFeed.findIndex(({ id }) => id === storyCard.id);

        if (cardIndex === -1) {
          return;
        }

        state.tagsFeed[cardIndex] = {
          ...state.tagsFeed[cardIndex],
          dateToDisplay: storyCard.dateToDisplay,
        };
      })
      .addCase(togglePublishOnCard.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        const { id: storyCardId, status, postingTime } = payload;

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        if (state.filters?.status?.length && !state.filters.status.includes(status)) {
          state.tagsFeed = state.tagsFeed.filter(({ id }) => id !== storyCardId);
          return;
        }

        card.status = status;
        card.postingTime = postingTime;
      })
      .addCase(toggleCardSection.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        const { id: storyCardId, section } = payload;

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        card.section = section;
      })
      .addCase(addCardToBookmarks.fulfilled, (state, action) => {
        const { payload, storyCardId } = action.payload;

        if (!payload) {
          return;
        }

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        card.isBookmarked = true;
      })
      .addCase(removeCardFromBookmarks.fulfilled, (state, action) => {
        const { payload, storyCardId } = action.payload;

        if (!payload) {
          return;
        }

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        card.isBookmarked = false;
      })
      .addCase(pollVote.fulfilled, (state, action) => {
        const { payload, storyCardId, selectedVoteIds } = action.payload;

        if (!payload) {
          return;
        }

        const card = state.tagsFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        pollVoteReducer({ card, selectedVoteIds });
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { deleted, eventId } = action.payload;

        if (!deleted) {
          return;
        }

        state.tagsFeed
          .filter((card) => card.eventId === eventId)
          .forEach((card) => {
            card.event = null;
          });
      })
      .addMatcher(
        isAnyOf(updateCardEvent.fulfilled, eventUpdateChannels.fulfilled),
        (state, action) => {
          state.tagsFeed
            .filter(({ eventId }) => eventId === action.payload.id)
            .forEach((card) => {
              card.event = action.payload;
            });
        }
      );
  },
});

export default tagsFeedSlice.reducer;
